/// <reference path='clientPoint.module.ts' />
/// <reference path='../../../../typings/index.d.ts' />

module Meevo.Plugins.ClientPoint {

    import ClientPointSettings = Meevo.Model.ClientPointSettings;
    import PointAdjustmentTypeEnum = Meevo.Model.PointAdjustmentTypeEnum;
    import IClientPoint = Meevo.Model.IClientPoint;
    import IClientPointHistoryItem = Meevo.Model.IClientPointHistoryItem;

    //NULL - ALL; 1 - Active; 2 - Expired; 3 - Added; 4 - Removed; 5 - Earned; 6 - Redeemed
    export enum ClientPointsStatusEnum {
        Active = 1,
        Expired = 2,
        Added = 3,
        Removed = 4,
        Earned = 5,
        Redeemed = 6
    }

    export interface IClientPointService {
        getSettings(): ng.IPromise<any>;
        getEnumStringName(pointAdjustmentType: PointAdjustmentTypeEnum, adjustmentAmount?: number, pointPromotionType?: Meevo.Model.PointPromotionTypeEnum): string;
        getGeneralInformation(): ng.IPromise<IClientPoint>;
        getHistory(status: ClientPointsStatusEnum | null): ng.IPromise<IClientPointHistoryItem[]>;
        convertPointsToDollarValue(points: number, maintainPrecision: boolean): number;
        convertDollarsToPoints(dollars: number): number;
        settings: ClientPointSettings;
        clientPoint: IClientPoint;
    }

    export class ClientPointService implements IClientPointService {
        private _settings: ClientPointSettings;
        private _clientPoint: IClientPoint;
        private _pointAdjustmentTypeObject = {
            [PointAdjustmentTypeEnum.Manual]: 'LABEL_MANUAL_ADJUSTMENT',
            [PointAdjustmentTypeEnum.Register]: 'LABEL_REGISTER_REDEMPTION',
            [PointAdjustmentTypeEnum.PointsReset]: 'LABEL_POINTS_RESET',
            ['EARNED']: 'LABEL_REGISTER_EARNED'
        };
        private _conversionPrecision: number = 6;

        constructor (
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected $translate: angular.translate.ITranslateService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected roundingService: Meevo.Components.Common.RoundingService
        ) { }

        /**
         * Get tenant settings
         * @returns {ng.IPromise} promise
         */
        getSettings(): ng.IPromise<any> {
            return this.dataService.customMethodData('/api/Settings/ClientPoint', 'GET', null).then((response) => {
                this.settings = response.data;
            });
        }

        /**
         * Convert points to dollar value
         * @param {number} points
         * @returns {number}
         */
        convertPointsToDollarValue(points: number = 0, maintainPrecision: boolean): number {
            if (maintainPrecision) {
                return this.roundingService.normalizeDecimalPlaces(points * this.settings.dollarsValue / this.settings.pointsValue, this._conversionPrecision);
            }

            const multiplier = Math.pow(10, this.identityService.current().decimalPositions);
            let ret = (points * this.settings.dollarsValue / this.settings.pointsValue) * multiplier;
            ret = Math.floor(ret) / multiplier;
            return this.roundingService.normalizeDecimalPlaces(ret);
        }

        /**
         * Convert dollars to points
         * @param {number} dollars
         * @returns {number}
         */
        convertDollarsToPoints(dollars: number = 0, arePointsRounded: boolean = false): number {
            return arePointsRounded
                ? Math.round(dollars * this.settings.pointsValue / this.settings.dollarsValue)
                : Math.floor(dollars * this.settings.pointsValue / this.settings.dollarsValue);
        }

        /**
         * Convert point promotion type enum value to string value
         * @param {PointPromotionTypeEnum} pointPromotionType
         */
        private getPointPromotionTypeActionText(pointPromotionType?: Meevo.Model.PointPromotionTypeEnum): string {
            switch (pointPromotionType)
            {
                case Meevo.Model.PointPromotionTypeEnum.Birthday:
                    return 'LABEL_PROMO_BIRTHDAY';
                case Meevo.Model.PointPromotionTypeEnum.BookingAppointmentOnline:
                    return 'LABEL_PROMO_BOOKING_APPOINTMENT_ONLINE';
                case Meevo.Model.PointPromotionTypeEnum.ClientFirstVisit:
                    return 'LABEL_PROMO_CLIENT_FIRST_VISIT';
                case Meevo.Model.PointPromotionTypeEnum.GiftCardPurchase:
                    return 'LABEL_PROMO_GIFT_CARD_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.MembershipPurchase:
                    return 'LABEL_PROMO_MEMBERSHIP_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.NewService:
                    return 'LABEL_PROMO_NEW_SERVICE';
                case Meevo.Model.PointPromotionTypeEnum.NewServiceCategory:
                    return 'LABEL_PROMO_NEW_SERVICE_CATEGORY';
                case Meevo.Model.PointPromotionTypeEnum.PackagePurchase:
                    return 'LABEL_PROMO_PACKAGE_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.RebookingNextAppointment:
                    return 'LABEL_PROMO_REBOOKING_NEXT_APPOINTMENT';
                case Meevo.Model.PointPromotionTypeEnum.RebookingNextAppointmentFirst:
                    return 'LABEL_PROMO_REBOOKING_NEXT_APPOINTMENT_FIRST';
                case Meevo.Model.PointPromotionTypeEnum.RebookingNextAppointmentAny:
                    return 'LABEL_PROMO_REBOOKING_NEXT_APPOINTMENT_ANY';
                case Meevo.Model.PointPromotionTypeEnum.ReferringAnotherClient:
                    return 'LABEL_PROMO_REFERRING_ANOTHER_CLIENT';
                case Meevo.Model.PointPromotionTypeEnum.RetailPurchase:
                    return 'LABEL_PROMO_RETAIL_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.RetailPurchaseMinimumAmount:
                    return 'LABEL_PROMO_RETAIL_PURCHASE_MINIMUM_AMOUNT';
                case Meevo.Model.PointPromotionTypeEnum.ServiceCategoryPurchaseMinimumTimes:
                    return 'LABEL_PROMO_SERVICE_CATEGORY_MINIMUM_TIMES';
                case Meevo.Model.PointPromotionTypeEnum.ServicePurchase:
                    return 'LABEL_PROMO_SERVICE_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.ServicePurchaseMinimumAmount:
                    return 'LABEL_PROMO_SERVICE_PURCHASE_MINIMUM_AMOUNT';
                case Meevo.Model.PointPromotionTypeEnum.ServicePurchaseMinimumTimes:
                    return 'LABEL_PROMO_SERVICE_PURCHASE_MINIMUM_TIMES';
                case Meevo.Model.PointPromotionTypeEnum.ServiceCategoryFirstTime:
                    return 'LABEL_PROMO_SERVICE_CATEGORY_PURCHASE';
                case Meevo.Model.PointPromotionTypeEnum.MembershipEFT:
                    return 'LABEL_PROMO_MEMBERSHIP_EFT';
                case Meevo.Model.PointPromotionTypeEnum.GiftCardPurchaseTimesPurchasePrice:
                    return 'LABEL_PROMO_GIFT_CARD_PURCHASE_TIMES_PURCHASE_PRICE';
            }
            return null;
        }

        /**
         * Convert enum value to appropriate string value
         * @param {PointAdjustmentTypeEnum} pointAdjustmentType
         * @param {adjustmentAmount} adjustmentAmount
         * @param {PointPromotionTypeEnum} pointPromotionType
         */
        getEnumStringName(pointAdjustmentType: PointAdjustmentTypeEnum, adjustmentAmount?: number, pointPromotionType?: Meevo.Model.PointPromotionTypeEnum): string {
            if ((pointAdjustmentType === PointAdjustmentTypeEnum.Register || pointAdjustmentType === PointAdjustmentTypeEnum.Automatic) &&
               typeof adjustmentAmount == 'number' && adjustmentAmount >= 0 ) {
                let actionText = this.$translate.instant(this._pointAdjustmentTypeObject['EARNED']);
                let pointPromotionActionText = this.getPointPromotionTypeActionText(pointPromotionType);
                if (pointPromotionActionText != null) {
                    actionText += ': ' + this.$translate.instant(pointPromotionActionText);
                }
                return actionText;
            } else {
                return this.$translate.instant(this._pointAdjustmentTypeObject[pointAdjustmentType]);
            }

        }
        /**
         * Get general inforamtion about Client releted to points
         * @returns {ng.IPromise<IClientPoint>}
         */
        getGeneralInformation(): ng.IPromise<IClientPoint> {
            return this.dataService.customMethodData(`api/ClientPoint/${this.clientPoint.client_TId}`, 'GET', null).then((response) => {
                this.clientPoint = response.data;

                return this.clientPoint;
            });
        }

        /**
         * Get information about adding/removing points
         * @returns {ng.IPromise<IClientPointHistoryItem[]>}
         */
        getHistory(status: ClientPointsStatusEnum = null): ng.IPromise<IClientPointHistoryItem[]> {
            return this.dataService.customMethodData(`api/ClientPoint/${this.clientPoint.client_TId}/StatusHistory`, 'POST', {status}).then((response) => {
                return response.data.history;
            });
        }

        get settings(): ClientPointSettings {
            return this._settings;
        }

        set settings(value: ClientPointSettings) {
            this._settings = value;
        }

        set clientPoint(clientPoint: IClientPoint) {
            this._clientPoint = clientPoint;
        }

        get clientPoint(): IClientPoint {
            return this._clientPoint;
        }

        static factory(): Function {
            let instance = (
                dataService: Meevo.Components.BaseClasses.IDataService,
                $translate: angular.translate.ITranslateService,
                identityService: Meevo.Components.Common.IIdentityService,
                roundingService: Meevo.Components.Common.RoundingService
            ) => {
                return new ClientPointService(dataService, $translate, identityService, roundingService);
            };

            instance.$inject = ['meevo.components.common.DataService', '$translate', 'meevo.components.common.IdentityService', 'meevo.components.common.RoundingService'];
            return instance;
        }
    }

    angular.module('meevo.plugins.clientPoint')
        .service('meevo.plugins.clientPoint.ClientPointService', ClientPointService.factory());
}
